import React, { useLayoutEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import {
  AddressTypes,
  ModalRow,
  theme,
  useSafeExpoNavigation,
  useSafeNavigation,
} from '@sg/garnish';
import { Map } from '@sg/radar-fe';

import { getEnvVars } from '@order/utils';

import { DeliveryLocationCard } from '../DeliveryLocationCard';
import { RestaurantLocationCard } from '../RestaurantLocationCard';
import { ConfirmLocationLayoutFooter } from './ConfirmLocationLayoutFooter';

import type { ConfirmLocationLayoutProps } from './ConfirmLocationLayout.types';

export const ConfirmLocationLayoutXS = (props: ConfirmLocationLayoutProps) => {
  const {
    deliveryAddress,
    isLoading,
    onContinue,
    onMoreLocations,
    orderType,
    pins,
    region,
    restaurant,
  } = props;

  const navigation = useSafeNavigation();
  const expoNavigation = useSafeExpoNavigation();
  const setOptions = navigation?.setOptions ?? expoNavigation?.setOptions;

  const { formatMessage } = useIntl();
  const isDelivery = orderType === AddressTypes.delivery;

  useLayoutEffect(() => {
    setOptions?.({
      headerTitle: formatMessage(messages.locationConfirmLocation, {
        channel: orderType,
      }),
      headerShown: true,
    });
  }, [formatMessage, orderType, setOptions]);

  return (
    <View testID="confirm-location.container-xs" style={styles.container}>
      <View style={styles.contentContainer}>
        <ModalRow style={styles.mapRow}>
          <View style={styles.mapContainer}>
            <Map publishableKey={RADAR_SDK_KEY} region={region} pins={pins} />
          </View>
        </ModalRow>

        <ModalRow style={styles.locationContainer}>
          {isDelivery && deliveryAddress ? (
            <DeliveryLocationCard deliveryAddress={deliveryAddress} />
          ) : (
            <RestaurantLocationCard orderType={orderType} restaurant={restaurant} />
          )}
        </ModalRow>
      </View>

      <ConfirmLocationLayoutFooter
        isLoading={isLoading}
        onMoreLocations={onMoreLocations}
        onContinue={onContinue}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  contentContainer: {
    flex: 1,
  },
  mapContainer: {
    flex: 1,
    borderRadius: theme.radius.medium,
    borderWidth: 1,
    borderColor: theme.colors.GRAY,
    overflow: 'hidden',
  },
  mapRow: {
    flex: 0.5,
    minHeight: 192,
  },
  locationContainer: {
    paddingVertical: theme.spacing['4'],
  },
});

// ─────────────────────────────────────────────────────────────────────────────

const { RADAR_SDK_KEY } = getEnvVars();

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  locationConfirmLocation: {
    defaultMessage: 'Confirm {channel} location',
    description: 'Location | Confirm Location',
  },
});
